import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Card, Progress, Space, Spin, Table, Tag, Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
  ACTIVE_RUN_STATUSES, REGION_LABELS, RUN_STATUS_CONFIG,
} from '../constants';
import { getApplyPercent, formatTimestamp, pickTimestamp } from '../runUtils';
import useSegmentationRunsQuery from './use-segmentation-runs-query';

const { Text } = Typography;

const STARTED_FIELDS = ['createdAt', 'startedAt', 'scoringStartedAt'];
const UPDATED_FIELDS = ['completedAt', 'appliedAt', 'updatedAt'];

const statusTag = (status) => {
  const config = RUN_STATUS_CONFIG[status] ?? { label: status ?? 'Unknown', color: 'default' };
  return <Tag color={config.color}>{config.label}</Tag>;
};

const RunsList = ({ onSelectRun }) => {
  // Socket keeps rows live; the interval is a reconnect fallback only.
  const {
    data: runs, isLoading, isFetching, refetch,
  } = useSegmentationRunsQuery({
    limit: 25,
    refetchInterval: 30000,
  });

  const dataSource = useMemo(
    () => (Array.isArray(runs) ? runs.map((run) => ({ ...run, key: run.id })) : []),
    [runs],
  );

  return (
    <Card
      className="segmentation-section"
      title="Recent runs"
      extra={(
        <Button
          icon={<ReloadOutlined />}
          onClick={() => refetch()}
          loading={isFetching}
        >
          Refresh
        </Button>
      )}
    >
      <Table
        size="middle"
        rowKey="id"
        loading={isLoading}
        dataSource={dataSource}
        onRow={(record) => ({
          onClick: () => onSelectRun(record.id),
          style: { cursor: 'pointer' },
        })}
        locale={{ emptyText: 'No runs yet — start one above.' }}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        columns={[
          {
            title: 'Region',
            dataIndex: 'region',
            key: 'region',
            render: (region) => <Tag>{REGION_LABELS[region] ?? region}</Tag>,
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
              <Space size="small">
                {statusTag(status)}
                {status === 'scoring' && <Spin size="small" />}
              </Space>
            ),
          },
          {
            title: 'Progress',
            key: 'progress',
            width: 220,
            render: (_, run) => {
              if (run.status === 'applying') {
                return <Progress percent={getApplyPercent(run)} size="small" status="active" />;
              }
              if (run.status === 'completed') {
                return <Progress percent={100} size="small" />;
              }
              if (run.status === 'failed') {
                return <Progress percent={getApplyPercent(run)} size="small" status="exception" />;
              }
              if (ACTIVE_RUN_STATUSES.includes(run.status)) {
                return <Text type="secondary">Awaiting apply</Text>;
              }
              return <Text type="secondary">—</Text>;
            },
          },
          {
            title: 'Started',
            key: 'started',
            render: (_, run) => formatTimestamp(pickTimestamp(run, STARTED_FIELDS)),
          },
          {
            title: 'Updated',
            key: 'updated',
            render: (_, run) => formatTimestamp(pickTimestamp(run, UPDATED_FIELDS)),
          },
          {
            title: '',
            key: 'action',
            align: 'right',
            render: (_, run) => (
              <Button type="link" onClick={(e) => { e.stopPropagation(); onSelectRun(run.id); }}>
                View
              </Button>
            ),
          },
        ]}
      />
    </Card>
  );
};

RunsList.propTypes = {
  onSelectRun: PropTypes.func.isRequired,
};

export default RunsList;
